import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaBriefcase, FaPlus, FaEdit, FaTrash, FaCheckCircle, FaTimesCircle, FaClock, FaMapMarkerAlt, FaMoneyBillWave, FaListUl, FaUsers, FaArrowRight, FaTimes } from 'react-icons/fa';
import DataTable from '../../SuperAdmin/shared/DataTable';
import Loader from '../../shared/Loader';
import { showSuccess, showError, showConfirm, showLoading, closeLoading } from '../../../utils/Alert';
import './JobLifecycle.css';

const JobLifecycle = () => {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingJob, setEditingJob] = useState(null);
    const [companyInfo, setCompanyInfo] = useState(null);
    const navigate = useNavigate();

    const initialFormState = {
        title: '',
        location: '',
        type: 'Full-time',
        category: '',
        salaryRange: '',
        experience: 'Entry Level',
        description: '',
        requirements: [''],
        benefits: ['']
    };

    const [formData, setFormData] = useState(initialFormState);

    useEffect(() => {
        fetchJobs();
        fetchCompany();
    }, []);

    const fetchCompany = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/recruiter/company', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCompanyInfo(res.data);
        } catch (err) {
            console.error("Company check failed:", err);
        }
    };

    const fetchJobs = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/recruiter/jobs', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setJobs(res.data);
        } catch (err) {
            console.error("Error fetching jobs:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleArrayChange = (index, value, field) => {
        const newArray = [...formData[field]];
        newArray[index] = value;
        setFormData(prev => ({ ...prev, [field]: newArray }));
    };

    const addArrayItem = (field) => {
        setFormData(prev => ({ ...prev, [field]: [...prev[field], ''] }));
    };

    const removeArrayItem = (index, field) => {
        const newArray = [...formData[field]];
        newArray.splice(index, 1);
        setFormData(prev => ({ ...prev, [field]: newArray }));
    };

    const standardizedCategories = [
        "IT & Software", "Healthcare & Medical", "Finance & Accounting", "Engineering & Manufacturing",
        "Education & Training", "Sales & Marketing", "Human Resources", "Administration & Office Support",
        "Customer Service", "Hospitality & Tourism", "Logistics & Supply Chain", "Design & Creative",
        "Media & Communications", "Legal", "Science & Research", "Construction & Trades",
        "Retail & Consumer Goods", "Data Science & Analytics", "Banking & Insurance", "Telecommunications",
        "Government & Public Sector", "NGO & Non-Profit", "Real Estate", "Automotive",
        "Agriculture & Farming", "Energy & Utilities", "Fashion & Apparel", "Food & Beverage",
        "Security & Law Enforcement", "Aviation & Aerospace", "Marine & Shipping", "Sports & Fitness",
        "Gaming & Animation", "Digital Marketing", "Content Writing", "Travel & Leisure",
        "E-commerce", "Cybersecurity", "Cloud Computing", "Quality Assurance",
        "Project Management", "Art & Photography", "Music & Entertainment", "Interior Design",
        "Social Services", "Mining & Extraction", "Event Management", "Others"
    ].sort();

    const openModal = (job = null) => {
        if (!job && (!companyInfo || !companyInfo.name)) {
            showError("Branding Incomplete", "Please complete your company branding profile before listing new job opportunities.");
            return;
        }

        if (job) {
            setEditingJob(job);
            setFormData({
                title: job.title,
                location: job.location,
                type: job.type,
                category: job.category || '',
                salaryRange: job.salaryRange || job.salary || '',
                experience: job.experience || 'Entry Level',
                description: job.description,
                requirements: Array.isArray(job.requirements) && job.requirements.length > 0 ? job.requirements : [''],
                benefits: Array.isArray(job.benefits) && job.benefits.length > 0 ? job.benefits : [''],
            });
        } else {
            setEditingJob(null);
            setFormData(initialFormState);
        }
        setIsModalOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('token');
        showLoading(editingJob ? 'Updating Job Posting...' : 'Publishing Job Listing...');
        try {
            if (editingJob) {
                await axios.put(`/api/recruiter/jobs/${editingJob._id}`, formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } else {
                await axios.post('/api/recruiter/jobs', formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }
            setIsModalOpen(false);
            closeLoading();
            showSuccess(editingJob ? 'Listing Updated' : 'Job Published', editingJob ? 'The job listing has been updated successfully.' : 'Your new job listing has been submitted for review.');
            fetchJobs();
        } catch (err) {
            closeLoading();
            showError('Save Failed', err.response?.data?.message || 'Failed to save job listing');
        }
    };

    const handleDelete = async (jobId) => {
        const confirmed = await showConfirm("Archive Job", "Are you sure you want to archive this listing?");
        if (!confirmed.isConfirmed) return;
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`/api/recruiter/jobs/${jobId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            showSuccess("Listing Archived", "The job listing has been successfully moved to archives.");
            fetchJobs();
        } catch (err) {
            showError("Decline Failed", "Could not archive the job listing.");
        }
    };

    const jobColumns = [
        {
            header: 'Position',
            key: 'title',
            render: (row) => (
                <div className="job-info">
                    <strong>{row.title}</strong>
                    <span className="job-meta"><FaMapMarkerAlt /> {row.location} • {row.type}</span>
                </div>
            )
        },
        {
            header: 'Status',
            key: 'status',
            render: (row) => (
                <span className={`status-badge ${row.status.toLowerCase()}`}>
                    {row.status === 'Active' && <FaCheckCircle />}
                    {row.status === 'Pending' && <FaClock />}
                    {row.status === 'Rejected' && <FaTimesCircle />}
                    {row.status}
                </span>
            )
        },
        {
            header: 'Compensation',
            key: 'salaryRange',
            render: (row) => (
                <span style={{ color: '#475569', fontSize: 13 }}>
                    <FaMoneyBillWave style={{ marginRight: 6, color: '#d4af37' }} />
                    {row.salaryRange || 'Not specified'}
                </span>
            )
        },
        {
            header: 'Applicants',
            key: 'applicantCount',
            render: (row) => (
                <button
                    className="applicant-count-btn"
                    onClick={() => navigate('/recruiter/applicants', { state: { jobId: row._id } })}
                    title="View Candidates"
                >
                    <FaUsers /> {row.applicantCount} Candidates
                </button>
            )
        },
        {
            header: 'Posted Date',
            key: 'postedAt',
            render: (row) => new Date(row.postedAt).toLocaleDateString()
        },
        {
            header: 'Actions',
            key: '_id',
            render: (row) => (
                <div className="action-btns">
                    <button className="icon-btn edit" onClick={() => openModal(row)} title="Edit Job"><FaEdit /></button>
                    <button className="icon-btn delete" onClick={() => handleDelete(row._id)} title="Archive Job"><FaTrash /></button>
                </div>
            )
        }
    ];

    if (loading) return <Loader />;

    const jobModal = isModalOpen && ReactDOM.createPortal(
        <div className={`jl-modal-overlay open`} onClick={() => setIsModalOpen(false)}>
            <div className="jl-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="jl-modal-header">
                    <h2>{editingJob ? <><FaEdit /> Edit Job Posting</> : <><FaPlus /> Create New Job Posting</>}</h2>
                    <button className="modal-close-btn" onClick={() => setIsModalOpen(false)}><FaTimes /></button>
                </div>

                <div className="jl-modal-body">
                    <p className="jl-modal-desc">
                        {editingJob
                            ? "Refine the details of your professional opportunity. Significant updates require re-approval."
                            : "Launch a new career pathway. Our team will review the listing before it goes live."
                        }
                    </p>

                    <form onSubmit={handleSubmit} className="jl-form">
                        <div className="jl-form-group jl-full-width">
                            <label className="jl-label">Job Title</label>
                            <input type="text" className="jl-input" name="title" value={formData.title} onChange={handleInputChange} placeholder="e.g. Senior Software Engineer" required />
                        </div>

                        <div className="jl-form-group">
                            <label className="jl-label">Location</label>
                            <input type="text" className="jl-input" name="location" value={formData.location} onChange={handleInputChange} placeholder="e.g. Bengaluru, Remote" required />
                        </div>

                        <div className="jl-form-group">
                            <label className="jl-label">Work Type</label>
                            <select className="jl-input" name="type" value={formData.type} onChange={handleInputChange}>
                                <option value="Full-time">Full-time</option>
                                <option value="Part-time">Part-time</option>
                                <option value="Contract">Contract</option>
                                <option value="Internship">Internship</option>
                                <option value="Remote">Remote</option>
                            </select>
                        </div>

                        <div className="jl-form-group jl-full-width">
                            <label className="jl-label">Job Category</label>
                            <select className="jl-input" name="category" value={formData.category} onChange={handleInputChange} required>
                                <option value="">Select Category</option>
                                {standardizedCategories.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        </div>

                        <div className="jl-form-group jl-full-width">
                            <label className="jl-label">Experience Bracket</label>
                            <select className="jl-input" name="experience" value={formData.experience} onChange={handleInputChange}>
                                <option value="Entry Level">Entry Level</option>
                                <option value="1-3 Years">1-3 Years</option>
                                <option value="3-5 Years">3-5 Years</option>
                                <option value="5+ Years">5+ Years</option>
                                <option value="Expert/Lead">Expert/Lead</option>
                            </select>
                        </div>

                        <div className="jl-form-group jl-full-width">
                            <label className="jl-label">Compensation / Salary Range</label>
                            <input type="text" className="jl-input" name="salaryRange" value={formData.salaryRange} onChange={handleInputChange} placeholder="e.g. $60k – $80k" />
                        </div>

                        <div className="jl-form-group jl-full-width">
                            <label className="jl-label">Job Description</label>
                            <textarea className="jl-input" name="description" value={formData.description} onChange={handleInputChange} rows="5" style={{ minHeight: '110px', resize: 'vertical' }} placeholder="Outline the role, team, and day-to-day responsibilities..." required></textarea>
                        </div>

                        <div className="jl-form-group jl-full-width">
                            <label className="jl-label"><FaListUl style={{ marginRight: 6 }} /> Key Requirements</label>
                            <div className="jl-array-container">
                                {formData.requirements.map((req, index) => (
                                    <div key={index} className="jl-array-row">
                                        <input className="jl-input" value={req} onChange={(e) => handleArrayChange(index, e.target.value, 'requirements')} placeholder="e.g. 5+ years of React experience" />
                                        {formData.requirements.length > 1 && (
                                            <button type="button" onClick={() => removeArrayItem(index, 'requirements')} className="jl-remove-btn"><FaTimes /></button>
                                        )}
                                    </div>
                                ))}
                                <button type="button" className="jl-add-btn" onClick={() => addArrayItem('requirements')}>+ Add Requirement</button>
                            </div>
                        </div>

                        <div className="jl-modal-actions">
                            <button type="button" className="jl-btn-secondary" onClick={() => setIsModalOpen(false)}>Cancel</button>
                            <button type="submit" className="jl-btn-gold">
                                {editingJob ? 'Update Listing' : 'Publish Opportunity'} <FaArrowRight style={{ marginLeft: 10 }} />
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>,
        document.body
    );

    return (
        <div className="dashboard-overview fade-in">
            <div className="dashboard-header">
                <div>
                    <h1 className="page-title">Job Lifecycle Management</h1>
                    <p className="page-subtitle">Manage your active listings and create new career opportunities.</p>
                </div>
                <button className="primary-btn pulse-on-hover" onClick={() => openModal()}>
                    <FaPlus /> Create New Job
                </button>
            </div>

            <div className="premium-card">
                <DataTable
                    title="Your Job Listings"
                    columns={jobColumns}
                    data={jobs}
                    filterOptions={[
                        { label: 'All Jobs', value: 'all' },
                        { label: 'Active', value: 'Active' },
                        { label: 'Pending', value: 'Pending' },
                        { label: 'Archived', value: 'Archived' }
                    ]}
                />
            </div>

            {jobModal}
        </div>
    );
};

export default JobLifecycle;
